// A baobab — the iconic "upside-down tree": a massive, SMOOTH bottle-shaped
// trunk that flares at the roots, swells into a fat belly and tapers to a narrow
// neck, crowned by a few thick stubby branches splaying out under a broad,
// sparse, flat-topped canopy. Ported 1:1 from age-of-mcp's classic tree-baobab.ts,
// restoring its branch helper and crown loop.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

function partsFor(): Part[] {
  const bark     = "#ab926a";   // warm pale baobab bark
  const barkDark = "#937a55";   // shaded bark for flare + branches
  const parts: Part[] = [
    // flared root base
    { geometry: new THREE.CylinderGeometry(0.5, 0.7, 0.26, 9),
      color: barkDark, position: [0, 0.13, 0] },
    // fat belly — a vertically-stretched smooth bulb (the swollen trunk)
    { geometry: new THREE.SphereGeometry(0.54, 9, 7),
      color: bark, position: [0, 0.9, 0], scale: [1, 1.5, 1] },
    // neck tapering up to the crown
    { geometry: new THREE.CylinderGeometry(0.16, 0.46, 0.55, 8),
      color: bark, position: [0, 1.78, 0] },
  ];

  const topY = 2.0;
  // A thick stubby branch from the crown, splaying up and out.
  const branch = (a: number, L: number, tilt: number): Part => ({
    geometry: new THREE.CylinderGeometry(0.05, 0.13, L, 5),
    color: barkDark,
    position: [
      Math.cos(a) * Math.sin(tilt) * L / 2,
      topY + Math.cos(tilt) * L / 2,
      Math.sin(a) * Math.sin(tilt) * L / 2,
    ],
    rotation: [Math.sin(a) * tilt, 0, -Math.cos(a) * tilt],
  });

  // Broad, sparse, FLAT crown: a flattened foliage clump at each branch tip,
  // pushed out past the tip so the canopy spreads wide like a real baobab.
  const N = 6;
  for (let i = 0; i < N; i++) {
    const a    = (i / N) * Math.PI * 2 + 0.4;
    const tilt = 0.9;           // splay wide and low -> spreading crown
    const L    = 0.5;
    parts.push(branch(a, L, tilt));
    const tx = Math.cos(a) * Math.sin(tilt) * L;
    const tz = Math.sin(a) * Math.sin(tilt) * L;
    const ty = topY + Math.cos(tilt) * L;
    parts.push({
      geometry: new THREE.IcosahedronGeometry(0.32, 0),
      color: i % 2 ? "#6b8f3e" : "#79a048",
      position: [tx * 1.2, ty + 0.04, tz * 1.2],
      scale: [1.15, 0.5, 1.15],  // flatten -> broad thin canopy
    });
  }
  // Centre crown disc filling the top between the splayed clumps.
  parts.push({
    geometry: new THREE.IcosahedronGeometry(0.42, 0),
    color: "#6f9442",
    position: [0, topY + 0.22, 0],
    scale: [1.35, 0.5, 1.35],
  });
  return parts;
}

export const treeBaobab = defineModel({
  id: "tree-baobab",
  batchId: "standard",
  name: "Tree baobab",
  tags: ["scenery","tree","baobab","low-poly","age-of-mcp","classic"],
  summary: "Low-poly tree baobab.",
  description: "Ported from the age-of-mcp gallery.",
  author: "age-of-mcp",
  footprint: 0.9,
  parts: () => partsFor(),
  animations: [],
});
